import { Button, Flex, PopoverProps, Text } from "@chakra-ui/react";

import { useTranslation } from "react-i18next";
import { PreviewReleaseButton } from "./PreviewReleaseButton";
import { t } from "i18next";
import { Release, ReleasePricingType } from "../../../types";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import {
  NavigationTrigger,
  ReaderNavigationContext,
} from "../../../screens/reader/types";
import { useReleasePricingInfo } from "../../../hooks/useReleasePricingInfo";
import { useGetNavigationContext } from "../../../hooks/useGetNavigationContext";
import {
  FunnelConfigTypes,
  FunnelContextArea,
  FunnelNavigationContext,
  EventContextArea,
  FunnelOnSuccessResult,
} from "../../funnel/types";
import {
  ContextualCTAButton,
  DropdownOption,
} from "../../buttons/ContextualCTAButton";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { useNavigateToReaderWithContext } from "../../../screens/reader/hooks/useNavigateToReaderWithContext";
import { useParams } from "react-router";
import { useSlideInMenu } from "../../slideInMenu/hooks/useSlideInMenu";
import { ClaimWithGoldButton } from "../../cards/ClaimWithGoldButton";
import { markPendingGoldReleaseAutoPurchase } from "../../payments/utils/pendingGoldReleaseAutoPurchase";

export interface ReleaseActionsButtonsProps {
  release: Release;
  setFunnelConfig: (config: FunnelConfigTypes) => void;
  navigationContext?: ReaderNavigationContext;
  primaryButtonMaxWidth?: string | Record<string, string>;
  primaryButtonHeight?: string | number;
  hidePreviewButton?: boolean;
  onlyShowALCActions?: boolean;
  buttonSize?: "sm" | "md" | "lg";
  contextArea?: FunnelContextArea;
  eventContextArea?: EventContextArea;
  contextualButtonDirection?: PopoverProps["placement"];
  escapeContainerBounds?: boolean;
  enableFlipAndPreventOverflow?: boolean;
  wrapButtons?: boolean;
}

export function ReleaseActionsButtons({
  release,
  setFunnelConfig,
  primaryButtonMaxWidth,
  navigationContext,
  hidePreviewButton = false,
  onlyShowALCActions = false,
  buttonSize,
  contextArea = FunnelContextArea.releaseDetails,
  eventContextArea = EventContextArea.releaseDetails,
  contextualButtonDirection = "bottom",
  escapeContainerBounds = false,
  enableFlipAndPreventOverflow = false,
  wrapButtons = false,
}: ReleaseActionsButtonsProps) {
  const releasePricingInfo = useReleasePricingInfo(release);
  const { free_page_count, is_pod_available, pod_price } = release;

  const funnelContext = useGetNavigationContext({
    contextArea: eventContextArea,
    release,
  });

  // Create funnel context for release purchase with pricing information
  const funnelContextForReleasePurchase: FunnelNavigationContext = {
    ...funnelContext,
    data: {
      ...funnelContext.data,
      ppbGoldPricePoint: releasePricingInfo.goldPrice
        ? releasePricingInfo.goldPrice * 100
        : undefined,
      ppbPricePoint: releasePricingInfo.regularPrice
        ? releasePricingInfo.regularPrice * 100
        : undefined,
      hasSubscribeOffer: !!(
        releasePricingInfo.goldPrice &&
        releasePricingInfo.regularPrice &&
        releasePricingInfo.goldPrice !== releasePricingInfo.regularPrice
      ),
      premiumOnly: release.premium_only ? 1 : 0,
    },
  };

  const { regularPrice, goldPrice, salePrice, goldSalePrice, isOnSale, type } =
    releasePricingInfo;

  const setFunnelConfigWithContext = (config: FunnelConfigTypes) => {
    setFunnelConfig({
      ...config,
      context: {
        ...config.context,
        modalContext: contextArea,
      },
    });
  };

  const continueGoldSubscriptionToReleasePurchase = (
    result: FunnelOnSuccessResult,
  ) => {
    markPendingGoldReleaseAutoPurchase(release.id);
    setFunnelConfigWithContext({
      type: FUNNEL_TYPES.purchaseRelease,
      context: {
        ...result.context,
        navigationContext: funnelContextForReleasePurchase,
        purchaseRelease: release,
      },
      options: {
        ...result.options,
        onSuccess: undefined,
        autoPurchaseReleaseWithSavedPaymentMethod: true,
      },
    });
  };

  const reopenGoldSubscriptionAfterAuth = (result: FunnelOnSuccessResult) => {
    setFunnelConfigWithContext({
      type: FUNNEL_TYPES.subscription,
      context: {
        ...result.context,
        navigationContext: funnelContextForReleasePurchase,
        purchaseRelease: release,
      },
      options: {
        ...result.options,
        skipOfferPaywallModal: true,
        onSuccess: continueGoldSubscriptionToReleasePurchase,
        componentProps: {
          ...result.options?.componentProps,
          forceCheckoutVariant: "v2",
        },
      },
    });
  };

  const handleGoldSubscriptionSuccess = (result: FunnelOnSuccessResult) => {
    if (
      result.lastFunnelType &&
      [
        FUNNEL_TYPES.signup,
        FUNNEL_TYPES.signupEmailPassword,
        FUNNEL_TYPES.login,
      ].includes(result.lastFunnelType)
    ) {
      reopenGoldSubscriptionAfterAuth(result);
      return;
    }

    continueGoldSubscriptionToReleasePurchase(result);
  };

  switch (type) {
    case ReleasePricingType.ClaimableGoldMonthly:
      // Gold user, release is an active monthly reward, not yet claimed.
      // Deep-link to the rewards hub so they can complete the claim there.
      // Render even when onlyShowALCActions is set (used by surfaces like the
      // reader's BuyPreviewFooterBanner) — claiming is conceptually the ALC
      // equivalent for monthly drops, so callers that want only ALC-style
      // actions still want this CTA.
      return (
        <ClaimWithGoldButton
          width={wrapButtons ? "auto" : "100%"}
          maxWidth={primaryButtonMaxWidth}
          size={buttonSize}
        />
      );

    case ReleasePricingType.Free:
    case ReleasePricingType.Purchased:
    case ReleasePricingType.UnlockedWithGold:
      // If POD available, show both Read and Buy Print buttons
      if (is_pod_available && pod_price !== undefined) {
        return (
          !onlyShowALCActions && (
            <Flex
              flexDirection="row"
              width={wrapButtons ? "auto" : "100%"}
              gap="10px"
              flexWrap={wrapButtons ? "wrap" : "nowrap"}
            >
              <StartContinueReadingButton
                release={release}
                navigationContext={navigationContext}
                primaryButtonMaxWidth={primaryButtonMaxWidth}
                buttonSize={buttonSize}
                contextArea={contextArea}
              />
              <BuyPrintButton
                release={release}
                setFunnelConfig={setFunnelConfigWithContext}
                funnelContext={funnelContext}
                buttonSize={buttonSize}
              />
            </Flex>
          )
        );
      }

      return (
        !onlyShowALCActions && (
          <StartContinueReadingButton
            release={release}
            navigationContext={navigationContext}
            primaryButtonMaxWidth={primaryButtonMaxWidth}
            buttonSize={buttonSize}
            contextArea={contextArea}
          />
        )
      );

    case ReleasePricingType.FreeWithGold:
      // If print product available, show contextual dropdown with digital + print options
      if (is_pod_available && pod_price !== undefined) {
        const freeWithGoldOptions: DropdownOption[] = [
          {
            id: "free-with-gold",
            label: t("components.releases.actions.freeWithGoldDigital"),
            ctaText: t("components.releases.actions.getGold"),
            ctaVariant: "contextualCTAPrimary" as const,
            action: () =>
              setFunnelConfigWithContext({
                type: FUNNEL_TYPES.subscription,
                context: {
                  navigationContext: funnelContext,
                  purchaseRelease: release,
                },
                options: {
                  skipOfferPaywallModal: true,
                  onSuccess: handleGoldSubscriptionSuccess,
                  componentProps: {
                    forceCheckoutVariant: "v2",
                  },
                },
              }),
            testingSelector: "btnFreeWithGold",
          },
          createPrintOnDemandOption(
            release,
            setFunnelConfigWithContext,
            funnelContext,
          ),
        ];

        return (
          !onlyShowALCActions && (
            <Flex
              flexDirection="row"
              width={wrapButtons ? "auto" : "100%"}
              gap="10px"
              flexWrap={wrapButtons ? "wrap" : "nowrap"}
            >
              <ContextualCTAButton
                gc-testing-id="btnContextualFreeWithGold"
                variant="primary"
                width="100%"
                maxWidth={primaryButtonMaxWidth}
                size={buttonSize}
                placement={contextualButtonDirection}
                escapeContainerBounds={escapeContainerBounds}
                enableFlipAndPreventOverflow={enableFlipAndPreventOverflow}
                dropdownOptions={freeWithGoldOptions}
              >
                {t("components.releases.actions.purchaseOptions")}
              </ContextualCTAButton>
              {(free_page_count ?? 0) > 0 && !hidePreviewButton && (
                <PreviewButton
                  release={release}
                  navigationContext={navigationContext}
                  buttonSize={buttonSize}
                  contextArea={contextArea}
                />
              )}
            </Flex>
          )
        );
      }

      // No print product - show regular Free with Gold button
      return (
        !onlyShowALCActions && (
          <Flex
            flexDirection="row"
            width={wrapButtons ? "auto" : "100%"}
            gap="10px"
            flexWrap={wrapButtons ? "wrap" : "nowrap"}
          >
            <FreeWithGoldButton
              setFunnelConfig={setFunnelConfigWithContext}
              funnelContext={funnelContext}
              primaryButtonMaxWidth={primaryButtonMaxWidth}
              release={release}
              buttonSize={buttonSize}
              onSuccess={handleGoldSubscriptionSuccess}
            />
            {(free_page_count ?? 0) > 0 && !hidePreviewButton && (
              <PreviewButton
                release={release}
                navigationContext={navigationContext}
                buttonSize={buttonSize}
                contextArea={contextArea}
              />
            )}
          </Flex>
        )
      );

    case ReleasePricingType.Regular:
    case ReleasePricingType.GoldDiscount:
    case ReleasePricingType.OnGoldSale:
    case ReleasePricingType.OnSale:
    case ReleasePricingType.OnSaleAndAvailableGoldDiscount:
    case ReleasePricingType.OnSaleWithGoldDiscount:
      // If print product available, show contextual dropdown with digital + print options
      if (is_pod_available && pod_price !== undefined) {
        const regularPurchaseOptions: DropdownOption[] = [
          {
            id: "digital-purchase",
            label: (
              <PriceLabel
                price={(isOnSale ? salePrice : regularPrice) ?? 0.0}
                text={isOnSale ? "Digital - Sale price" : "Digital purchase"}
              />
            ),
            ctaText: "Buy",
            ctaVariant: "contextualCTAPrimary" as const,
            action: () =>
              setFunnelConfigWithContext({
                type: FUNNEL_TYPES.purchaseRelease,
                context: {
                  navigationContext: funnelContextForReleasePurchase,
                  purchaseRelease: release,
                },
              }),
            testingSelector: "btnBuyRelease",
          },
          createPrintOnDemandOption(
            release,
            setFunnelConfigWithContext,
            funnelContext,
          ),
        ];

        return (
          <Flex
            flexDirection="row"
            width={wrapButtons ? "auto" : "100%"}
            gap="10px"
            flexWrap={wrapButtons ? "wrap" : "nowrap"}
          >
            <ContextualCTAButton
              gc-testing-id="btnContextualBuyRelease"
              variant="primary"
              width="100%"
              maxWidth={primaryButtonMaxWidth}
              size={buttonSize}
              placement={contextualButtonDirection}
              escapeContainerBounds={escapeContainerBounds}
              enableFlipAndPreventOverflow={enableFlipAndPreventOverflow}
              dropdownOptions={regularPurchaseOptions}
            >
              {t("components.releases.actions.purchaseOptions")}
            </ContextualCTAButton>
            {(free_page_count ?? 0) > 0 && !hidePreviewButton && (
              <PreviewButton
                release={release}
                navigationContext={navigationContext}
                buttonSize={buttonSize}
                contextArea={contextArea}
              />
            )}
          </Flex>
        );
      }

      // No print product - show regular Buy Now button
      return (
        <Flex
          flexDirection="row"
          width={wrapButtons ? "auto" : "100%"}
          gap="10px"
          flexWrap={wrapButtons ? "wrap" : "nowrap"}
        >
          <BuyNowButton
            setFunnelConfig={setFunnelConfigWithContext}
            funnelContext={funnelContextForReleasePurchase}
            primaryButtonMaxWidth={primaryButtonMaxWidth}
            release={release}
            buttonSize={buttonSize}
          />
          {(free_page_count ?? 0) > 0 && !hidePreviewButton && (
            <PreviewButton
              release={release}
              navigationContext={navigationContext}
              buttonSize={buttonSize}
              contextArea={contextArea}
            />
          )}
        </Flex>
      );

    case ReleasePricingType.AvailableGoldDiscount:
    case ReleasePricingType.OnSaleAndAvailableGoldDiscount:
    case ReleasePricingType.RegularWithAvailableOnGoldSale:
    case ReleasePricingType.onSaleWithAvailableOnGoldSale:
      const currentGoldPrice =
        type == ReleasePricingType.RegularWithAvailableOnGoldSale ||
        type == ReleasePricingType.onSaleWithAvailableOnGoldSale
          ? goldSalePrice
          : goldPrice;

      const goldDiscountOptions: DropdownOption[] = [
        {
          id: "gold-price",
          label: (
            <PriceLabel price={currentGoldPrice ?? 0.0} text="Gold price" />
          ),
          ctaText: "Join",
          ctaVariant: "secondary" as const,
          action: () => {
            markPendingGoldReleaseAutoPurchase(release.id);
            setFunnelConfigWithContext({
              type: FUNNEL_TYPES.subscription,
              context: {
                navigationContext: funnelContext,
                purchaseRelease: release,
              },
              options: {
                skipOfferPaywallModal: true,
                onSuccess: handleGoldSubscriptionSuccess,
                componentProps: {
                  forceCheckoutVariant: "v2",
                },
              },
            });
          },
          testingSelector: "btnBuyWithGold",
        },
        {
          id: "regular-price",
          label: (
            <PriceLabel
              price={(isOnSale ? salePrice : regularPrice) ?? 0.0}
              text={isOnSale ? "Sale price" : "Regular price"}
            />
          ),
          ctaText: "Buy",
          ctaVariant: "contextualCTAPrimary" as const,
          action: () =>
            setFunnelConfigWithContext({
              type: FUNNEL_TYPES.purchaseRelease,
              context: {
                navigationContext: funnelContextForReleasePurchase,
                purchaseRelease: release,
              },
            }),
          testingSelector: "btnBuyRelease",
        },
      ];

      if (is_pod_available && pod_price !== undefined) {
        goldDiscountOptions.push(
          createPrintOnDemandOption(
            release,
            setFunnelConfigWithContext,
            funnelContext,
          ),
        );
      }

      return (
        <Flex
          flexDirection="row"
          width={wrapButtons ? "auto" : "100%"}
          gap="10px"
          flexWrap={wrapButtons ? "wrap" : "nowrap"}
        >
          <ContextualCTAButton
            gc-testing-id="btnContextualBuyRelease"
            variant="primary"
            width="100%"
            maxWidth={primaryButtonMaxWidth}
            size={buttonSize}
            placement={contextualButtonDirection}
            escapeContainerBounds={escapeContainerBounds}
            enableFlipAndPreventOverflow={enableFlipAndPreventOverflow}
            dropdownOptions={goldDiscountOptions}
          >
            {t("components.releases.actions.purchaseOptions")}
          </ContextualCTAButton>
          {(free_page_count ?? 0) > 0 && !hidePreviewButton && (
            <PreviewButton
              release={release}
              navigationContext={navigationContext}
              buttonSize={buttonSize}
              contextArea={contextArea}
            />
          )}
        </Flex>
      );

    case ReleasePricingType.PpbOrFreeWithGold:
    case ReleasePricingType.OnSaleOrFreeWithGold:
      const freeWithGoldOptions: DropdownOption[] = [
        {
          id: "free-with-gold",
          label: t("components.releases.actions.freeWithGold"),
          ctaText: "Join",
          ctaVariant: "secondary" as const,
          action: () =>
            setFunnelConfigWithContext({
              type: FUNNEL_TYPES.subscription,
              context: {
                navigationContext: funnelContext,
                purchaseRelease: release,
              },
              options: {
                skipOfferPaywallModal: true,
                onSuccess: handleGoldSubscriptionSuccess,
                componentProps: {
                  forceCheckoutVariant: "v2",
                },
              },
            }),
        },
        {
          id: "regular-price",
          label: (
            <PriceLabel
              price={(isOnSale ? salePrice : regularPrice) ?? 0.0}
              text={isOnSale ? "Sale price" : "Regular price"}
            />
          ),
          ctaText: "Buy",
          ctaVariant: "contextualCTAPrimary" as const,
          action: () =>
            setFunnelConfigWithContext({
              type: FUNNEL_TYPES.purchaseRelease,
              context: {
                navigationContext: funnelContextForReleasePurchase,
                purchaseRelease: release,
              },
            }),
          testingSelector: "btnBuyRelease",
        },
      ];

      if (is_pod_available && pod_price !== undefined) {
        freeWithGoldOptions.push(
          createPrintOnDemandOption(
            release,
            setFunnelConfigWithContext,
            funnelContext,
          ),
        );
      }

      return (
        <Flex
          flexDirection="row"
          width={wrapButtons ? "auto" : "100%"}
          gap="10px"
          flexWrap={wrapButtons ? "wrap" : "nowrap"}
        >
          <ContextualCTAButton
            gc-testing-id="btnContextualBuyRelease"
            variant="primary"
            size={buttonSize}
            width="100%"
            maxWidth={primaryButtonMaxWidth}
            placement={contextualButtonDirection}
            escapeContainerBounds={escapeContainerBounds}
            enableFlipAndPreventOverflow={enableFlipAndPreventOverflow}
            dropdownOptions={freeWithGoldOptions}
          >
            {t("components.releases.actions.purchaseOptions")}
          </ContextualCTAButton>
          {(free_page_count ?? 0) > 0 && !hidePreviewButton && (
            <PreviewButton
              release={release}
              navigationContext={navigationContext}
              buttonSize={buttonSize}
              contextArea={contextArea}
            />
          )}
        </Flex>
      );
  }
}

interface StartContinuePreviewReadingButtonProps {
  release: Release;
  navigationContext?: ReaderNavigationContext;
  primaryButtonMaxWidth?: string | Record<string, string>;
  buttonSize?: "sm" | "md" | "lg";
}

function StartContinueReadingButton({
  release,
  navigationContext,
  primaryButtonMaxWidth,
  buttonSize,
  contextArea,
}: StartContinuePreviewReadingButtonProps & {
  contextArea: FunnelContextArea;
}) {
  const context =
    navigationContext ??
    rootComponentsStore.slideInMenuConfig.get()?.navigationContext;
  const { t } = useTranslation();
  const { user_read_status } = release;

  // Determine trigger based on contextArea
  const isReleaseDetails = contextArea === FunnelContextArea.releaseDetails;
  const continueTrigger = isReleaseDetails
    ? NavigationTrigger.continueReadingButtonReleaseDetails
    : NavigationTrigger.continueReadingButtonReleaseContextual;
  const startTrigger = isReleaseDetails
    ? NavigationTrigger.startReadingButtonReleaseDetails
    : NavigationTrigger.startReadingButtonReleaseContextual;

  const { goToCTAUrl } = useNavigateToReaderWithContext(
    {
      ...context,
      releaseToNavigate: release,
      trigger: user_read_status ? continueTrigger : startTrigger,
    },
    [release],
  );

  const { uuid } = useParams();
  const { onCloseMenu } = useSlideInMenu();
  const isCurrentRelease = release.key === uuid;

  return (
    <Button
      variant="primary"
      size={buttonSize}
      width="100%"
      maxWidth={primaryButtonMaxWidth}
      alignSelf="flex-start"
      gc-testing-id={
        user_read_status ? "btnContinueReading" : "btnStartReading"
      }
      onClick={isCurrentRelease ? onCloseMenu : goToCTAUrl}
    >
      {user_read_status
        ? t("components.releases.actions.continueReading")
        : t("components.releases.actions.startReading")}
    </Button>
  );
}

function PreviewButton({
  release,
  navigationContext,
  buttonSize = "md",
  contextArea,
}: {
  release: Release;
  navigationContext?: ReaderNavigationContext;
  buttonSize?: "sm" | "md" | "lg";
  contextArea: FunnelContextArea;
}) {
  const context =
    navigationContext ??
    rootComponentsStore.slideInMenuConfig.get()?.navigationContext;

  // Determine trigger based on contextArea
  const isReleaseDetails = contextArea === FunnelContextArea.releaseDetails;
  const previewTrigger = isReleaseDetails
    ? NavigationTrigger.previewReleaseButtonReleaseDetails
    : NavigationTrigger.previewReleaseButtonReleaseContextual;

  const { goToCTAUrl } = useNavigateToReaderWithContext(
    {
      ...context,
      releaseToNavigate: release,
      trigger: previewTrigger,
    },
    [release],
  );

  return <PreviewReleaseButton goToCTAUrl={goToCTAUrl} size={buttonSize} />;
}

function BuyNowButton({
  setFunnelConfig,
  funnelContext,
  primaryButtonMaxWidth,
  release,
  buttonSize,
}: {
  setFunnelConfig: (config: FunnelConfigTypes) => void;
  funnelContext: FunnelNavigationContext;
  primaryButtonMaxWidth?: string | Record<string, string>;
  release: Release;
  buttonSize?: "sm" | "md" | "lg";
}) {
  const { t } = useTranslation();

  return (
    <Button
      variant="primary"
      size={buttonSize}
      width="100%"
      maxWidth={primaryButtonMaxWidth}
      gc-testing-id="btnBuyRelease"
      onClick={() =>
        setFunnelConfig({
          type: FUNNEL_TYPES.purchaseRelease,
          context: {
            navigationContext: funnelContext,
            purchaseRelease: release,
          },
        })
      }
    >
      {t("components.releases.actions.buyNow")}
    </Button>
  );
}

function FreeWithGoldButton({
  setFunnelConfig,
  funnelContext,
  primaryButtonMaxWidth,
  release,
  buttonSize,
  onSuccess,
}: {
  setFunnelConfig: (config: FunnelConfigTypes) => void;
  funnelContext: FunnelNavigationContext;
  primaryButtonMaxWidth?: string | Record<string, string>;
  release: Release;
  buttonSize?: "sm" | "md" | "lg";
  onSuccess: (result: FunnelOnSuccessResult) => void;
}) {
  const { t } = useTranslation();

  return (
    <Button
      variant="primary"
      size={buttonSize}
      width="100%"
      maxWidth={primaryButtonMaxWidth}
      onClick={() =>
        setFunnelConfig({
          type: FUNNEL_TYPES.subscription,
          context: {
            navigationContext: funnelContext,
            purchaseRelease: release,
          },
          options: {
            skipOfferPaywallModal: true,
            onSuccess,
            componentProps: {
              forceCheckoutVariant: "v2",
            },
          },
        })
      }
    >
      {t("components.releases.actions.freeWithGold")}
    </Button>
  );
}

function BuyPrintButton({
  release,
  setFunnelConfig,
  funnelContext,
  buttonSize,
}: {
  release: Release;
  setFunnelConfig: (config: FunnelConfigTypes) => void;
  funnelContext: FunnelNavigationContext;
  buttonSize?: "sm" | "md" | "lg";
}) {
  const { t } = useTranslation();

  return (
    <Button
      variant="secondary"
      size={buttonSize}
      gc-testing-id="btnBuyPrint"
      onClick={() =>
        setFunnelConfig({
          type: FUNNEL_TYPES.purchasePod,
          context: {
            navigationContext: funnelContext,
            purchaseRelease: release,
          },
        })
      }
    >
      {t("components.releases.actions.buyPrint")}
    </Button>
  );
}

function PriceLabel({ text, price }: { text: string; price: number }) {
  return (
    <Flex
      transition="all 0.2s"
      fontWeight="400"
      fontSize="14px"
      letterSpacing="0.5"
      lineHeight="130%"
      color="dune.dune"
      gap="4px"
    >
      <Text> {text}</Text>
      <Text fontWeight="bold"> - ${price || "0.00"}</Text>
    </Flex>
  );
}

function createPrintOnDemandOption(
  release: Release,
  setFunnelConfig: (config: FunnelConfigTypes) => void,
  funnelContext: FunnelNavigationContext,
): DropdownOption {
  return {
    id: "print-on-demand",
    label: (
      <PriceLabel
        price={release.pod_price ?? 0}
        text={t("components.releases.actions.printEdition")}
      />
    ),
    ctaText: t("components.releases.actions.buyPrint"),
    ctaVariant: "contextualCTASecondary" as const,
    action: () => {
      setFunnelConfig({
        type: FUNNEL_TYPES.purchasePod,
        context: {
          navigationContext: funnelContext,
          purchaseRelease: release,
        },
      });
    },
    testingSelector: "btnBuyPrint",
  };
}
